import { useEffect, useMemo, useState } from "react";
import { mergeTeams } from "../utils/teamsPlayers";

// ---- Reconciliation helpers -----------------------------------------------

function suggestMergedName(teams) {
  const counts = {};
  for (const tm of teams) {
    const k = String(tm.name ?? "").trim().toLowerCase();
    counts[k] = (counts[k] ?? 0) + 1;
  }
  let best = null;
  let bestCount = 0;
  for (const [k, count] of Object.entries(counts)) {
    if (count > bestCount) {
      best = k;
      bestCount = count;
    }
  }
  return teams.find((tm) => tm.name?.toLowerCase() === best)?.name ?? teams[0]?.name ?? "";
}

function bestVariant(variants) {
  return variants.reduce((b, v) =>
    v.firstName.length + v.lastName.length > b.firstName.length + b.lastName.length ? v : b
  );
}

function reconcileRoster(teams) {
  const byShirt = new Map();
  const noShirt = [];

  for (const tm of teams) {
    for (const p of tm.players ?? []) {
      const shirt = String(p.shirtNumber ?? "").trim();
      const variant = {
        teamId: tm.id,
        teamName: tm.name,
        firstName: String(p.firstName ?? "").trim(),
        lastName: String(p.lastName ?? "").trim(),
        nickName: String(p.nickName ?? "").trim(),
        shirtNumber: shirt,
      };
      if (shirt) {
        if (!byShirt.has(shirt)) byShirt.set(shirt, []);
        byShirt.get(shirt).push(variant);
      } else {
        noShirt.push(variant);
      }
    }
  }

  const rows = [];

  // Numbered players sorted by jersey number
  for (const [shirt, variants] of [...byShirt.entries()].sort((a, b) => {
    const na = parseInt(a[0]) || 9999;
    const nb = parseInt(b[0]) || 9999;
    return na !== nb ? na - nb : a[0].localeCompare(b[0]);
  })) {
    const best = bestVariant(variants);
    const names = new Set(variants.map((v) => `${v.firstName}|${v.lastName}`.toLowerCase()));
    rows.push({
      key: `shirt_${shirt}`,
      shirtNumber: shirt,
      variants,
      firstName: best.firstName,
      lastName: best.lastName,
      nickName: best.nickName,
      include: true,
      hasConflict: names.size > 1,
    });
  }

  // Unnumbered players — dedupe by identity
  const seen = new Set();
  for (const v of noShirt) {
    const ident = `${v.firstName}|${v.lastName}`.toLowerCase().trim();
    if (!ident || ident === "|" || seen.has(ident)) continue;
    seen.add(ident);
    rows.push({
      key: `no_${ident}`,
      shirtNumber: "",
      variants: noShirt.filter(
        (x) => `${x.firstName}|${x.lastName}`.toLowerCase().trim() === ident
      ),
      firstName: v.firstName,
      lastName: v.lastName,
      nickName: v.nickName,
      include: true,
      hasConflict: false,
    });
  }

  return rows;
}

// ---- Step components -------------------------------------------------------

function Step1SelectTeams({ allTeams, selectedIds, onToggle, rosterWarning, t }) {
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();
  const visible = term
    ? allTeams.filter((tm) => tm.name?.toLowerCase().includes(term))
    : allTeams;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-base-content/70">{t("Select 2 or more team records to merge into one.")}</p>
      {rosterWarning && (
        <div className="rounded border border-warning bg-warning/10 px-3 py-2 text-xs">
          <p className="font-semibold text-warning-content mb-1">
            ⚠ {t("Roster differences detected between teams:")}
          </p>
          <ul className="space-y-0.5">
            {rosterWarning.map((r) => (
              <li key={r.name} className="text-base-content/80">
                <span className="font-medium">{r.name}</span>:{" "}
                {r.count} {t("players")}
                {r.shirts.length > 0 && (
                  <span className="text-base-content/50 ml-1">
                    [{r.shirts.join(", ")}]
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-base-content/60">
            {t("Review the roster in the next step before merging.")}
          </p>
        </div>
      )}
      <input
        type="text"
        className="input-generic"
        placeholder={t("Filter teams...")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      <div className="max-h-[45vh] overflow-y-auto rounded border border-base-300">
        {visible.length === 0 ? (
          <p className="p-3 text-sm text-base-content/60">{t("No teams found.")}</p>
        ) : (
          <ul>
            {visible.map((tm) => (
              <li key={tm.id}>
                <label className="flex cursor-pointer items-center gap-3 border-b border-base-200 px-3 py-2 hover:bg-base-200">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm rounded-none"
                    checked={selectedIds.has(tm.id)}
                    onChange={() => onToggle(tm.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{tm.name}</div>
                    <div className="text-xs text-base-content/50">
                      {tm.playerCount ?? 0} {t("players")}
                      {tm.lastSeenSeconds
                        ? ` · ${new Date(tm.lastSeenSeconds * 1000).toLocaleDateString()}`
                        : ""}
                    </div>
                  </div>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs text-base-content/60">
        {selectedIds.size} {t("teams selected")}
      </p>
    </div>
  );
}

function Step2ReconcileRoster({ mergedName, setMergedName, roster, setRoster, t }) {
  const updateRow = (key, field, value) =>
    setRoster((prev) =>
      prev.map((r) => (r.key === key ? { ...r, [field]: value } : r))
    );

  const conflictCount = roster.filter((r) => r.include && r.hasConflict).length;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="mb-0.5 text-xs font-semibold text-base-content/60">{t("Merged team name")}</p>
        <input
          type="text"
          className="input-generic"
          value={mergedName}
          onChange={(e) => setMergedName(e.target.value)}
          autoFocus
        />
      </div>
      {conflictCount > 0 && (
        <p className="rounded bg-warning/20 px-2 py-1 text-xs text-warning-content">
          ⚠ {conflictCount} {t("shirt number(s) have conflicting names — review below.")}
        </p>
      )}
      <div className="max-h-[50vh] overflow-y-auto rounded border border-base-300">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-base-200 text-xs uppercase text-base-content/60">
            <tr>
              <th className="px-2 py-1.5 text-center w-8">#</th>
              <th className="px-2 py-1.5 text-left">{t("Shirt")}</th>
              <th className="px-2 py-1.5 text-left">{t("First name")}</th>
              <th className="px-2 py-1.5 text-left">{t("Last name")}</th>
              <th className="px-2 py-1.5 text-left">{t("Nick")}</th>
            </tr>
          </thead>
          <tbody>
            {roster.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-3 text-center text-xs text-base-content/60">
                  {t("No players to reconcile.")}
                </td>
              </tr>
            ) : (
              roster.map((row) => (
                <tr
                  key={row.key}
                  className={`border-b border-base-200 ${!row.include ? "opacity-40" : ""}`}
                >
                  <td className="px-2 py-1 text-center">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs rounded-none"
                      checked={row.include}
                      onChange={(e) => updateRow(row.key, "include", e.target.checked)}
                    />
                  </td>
                  <td className="px-2 py-1 text-center text-xs font-mono text-base-content/60">
                    {row.shirtNumber || "—"}
                  </td>
                  <td className="px-1 py-1">
                    <div className="flex flex-col gap-0.5">
                      <input
                        type="text"
                        className={`input input-bordered input-xs w-full ${row.hasConflict ? "border-warning" : ""}`}
                        value={row.firstName}
                        onChange={(e) => updateRow(row.key, "firstName", e.target.value)}
                      />
                      {row.hasConflict && (
                        <div className="flex flex-wrap gap-1">
                          {[...new Set(row.variants.map((v) => v.firstName))].map((fn) => (
                            <button
                              key={fn}
                              type="button"
                              title={t("Use this name")}
                              className="rounded bg-base-200 px-1 py-0 text-[10px] hover:bg-primary hover:text-primary-content"
                              onClick={() => updateRow(row.key, "firstName", fn)}
                            >
                              {fn}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-1 py-1">
                    <div className="flex flex-col gap-0.5">
                      <input
                        type="text"
                        className={`input input-bordered input-xs w-full ${row.hasConflict ? "border-warning" : ""}`}
                        value={row.lastName}
                        onChange={(e) => updateRow(row.key, "lastName", e.target.value)}
                      />
                      {row.hasConflict && (
                        <div className="flex flex-wrap gap-1">
                          {[...new Set(row.variants.map((v) => v.lastName))].map((ln) => (
                            <button
                              key={ln}
                              type="button"
                              title={t("Use this name")}
                              className="rounded bg-base-200 px-1 py-0 text-[10px] hover:bg-primary hover:text-primary-content"
                              onClick={() => updateRow(row.key, "lastName", ln)}
                            >
                              {ln}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="text"
                      className="input input-bordered input-xs w-full"
                      value={row.nickName}
                      onChange={(e) => updateRow(row.key, "nickName", e.target.value)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-base-content/60">
        {roster.filter((r) => r.include).length} {t("players included")}
      </p>
    </div>
  );
}

function Step3Confirm({ mergedName, roster, selectedTeams, t }) {
  const includedCount = roster.filter((r) => r.include && (r.firstName || r.lastName)).length;
  const teamsToRemove = selectedTeams.length;
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2 text-sm">
        <li className="flex items-start gap-2">
          <span className="mt-0.5 text-success">✓</span>
          <span>
            {t("Create merged team")}{" "}
            <strong>"{mergedName}"</strong>{" "}
            {t("with")} {includedCount} {t("players")}
          </span>
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-0.5 text-success">✓</span>
          <span>
            {t("Remove")} {teamsToRemove} {t("duplicate team records")}
          </span>
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-0.5 text-success">✓</span>
          <span>{t("Consolidate duplicate player records and reassign playlist links")}</span>
        </li>
      </ul>
      <div className="rounded border border-base-300 bg-base-200/50 p-2 text-xs text-base-content/70">
        <p className="font-semibold">{t("Teams being merged:")}</p>
        <ul className="mt-1 list-inside list-disc">
          {selectedTeams.map((tm) => (
            <li key={tm.id}>
              {tm.name}{" "}
              <span className="text-base-content/40">
                ({tm.playerCount ?? 0} {t("players")})
              </span>
            </li>
          ))}
        </ul>
      </div>
      <p className="rounded bg-error/10 px-2 py-1 text-xs text-error">
        ⚠ {t("This cannot be undone.")}
      </p>
    </div>
  );
}

// ---- Main modal -----------------------------------------------------------

export default function MergeTeamsModal({ allTeams, rosterWarning, preSelectedIds, onClose, onDone, t }) {
  const [step, setStep] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => preSelectedIds ?? new Set());
  const [mergedName, setMergedName] = useState("");
  const [roster, setRoster] = useState([]);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);

  const selectedTeams = useMemo(
    () => allTeams.filter((tm) => selectedIds.has(tm.id)),
    [allTeams, selectedIds]
  );

  // Recompute suggested name & roster whenever selection changes (also on mount
  // when preSelectedIds is provided so the roster is ready for Step 2).
  useEffect(() => {
    if (selectedTeams.length < 2) return;
    setMergedName(suggestMergedName(selectedTeams));
    setRoster(reconcileRoster(selectedTeams));
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // When pre-selected IDs are provided (coming from MatchesList), trigger the
  // roster computation on mount since selectedIds doesn't "change" after init.
  useEffect(() => {
    if (!preSelectedIds || preSelectedIds.size < 2) return;
    const preTeams = allTeams.filter((tm) => preSelectedIds.has(tm.id));
    if (preTeams.length < 2) return;
    setMergedName(suggestMergedName(preTeams));
    setRoster(reconcileRoster(preTeams));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTeam = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const canNext1 = selectedIds.size >= 2;
  const canNext2 =
    mergedName.trim().length > 0 &&
    roster.some((r) => r.include && (r.firstName.trim() || r.lastName.trim()));

  const doMerge = async () => {
    setMerging(true);
    setError(null);
    try {
      const finalRoster = roster
        .filter((r) => r.include && (r.firstName.trim() || r.lastName.trim()))
        .map((r) => ({
          firstName: r.firstName.trim(),
          lastName: r.lastName.trim(),
          nickName: r.nickName.trim(),
          shirtNumber: r.shirtNumber,
        }));
      const result = await mergeTeams({
        mergedName: mergedName.trim(),
        mergedRoster: finalRoster,
        sourceTeamIds: [...selectedIds],
      });
      onDone(result);
    } catch (e) {
      console.log("Merge failed", e);
      setError(String(e?.message ?? e));
      setMerging(false);
    }
  };

  const STEP_TITLES = [
    t("Select Teams"),
    t("Reconcile Roster"),
    t("Confirm Merge"),
  ];

  return (
    <div className="modal modal-open z-[9999]">
      <div className="modal-box w-full max-w-2xl rounded-none shadow">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">{t("Merge Teams")}</h3>
            <p className="text-xs text-base-content/60">
              {t("Step")} {step} {t("of")} 3 — {STEP_TITLES[step - 1]}
            </p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        {/* Step content */}
        <div className="min-h-[200px]">
          {step === 1 && (
            <Step1SelectTeams
              allTeams={allTeams}
              selectedIds={selectedIds}
              onToggle={toggleTeam}
              rosterWarning={rosterWarning}
              t={t}
            />
          )}
          {step === 2 && (
            <Step2ReconcileRoster
              mergedName={mergedName}
              setMergedName={setMergedName}
              roster={roster}
              setRoster={setRoster}
              t={t}
            />
          )}
          {step === 3 && (
            <Step3Confirm
              mergedName={mergedName}
              roster={roster}
              selectedTeams={selectedTeams}
              t={t}
            />
          )}
        </div>

        {error && (
          <p className="mt-2 rounded bg-error/10 px-2 py-1 text-xs text-error">{error}</p>
        )}

        {/* Footer navigation */}
        <div className="mt-4 flex justify-between">
          <button
            className="btn btn-sm btn-ghost"
            onClick={() => (step === 1 ? onClose() : setStep((s) => s - 1))}
            disabled={merging}
          >
            {step === 1 ? t("Cancel") : `← ${t("Back")}`}
          </button>
          <div className="flex gap-2">
            {step < 3 && (
              <button
                className="btn btn-sm btn-primary"
                disabled={step === 1 ? !canNext1 : !canNext2}
                onClick={() => setStep((s) => s + 1)}
              >
                {t("Next")} →
              </button>
            )}
            {step === 3 && (
              <button
                className="btn btn-sm btn-error"
                onClick={doMerge}
                disabled={merging}
              >
                {merging ? t("Merging...") : t("Merge Teams")}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose} />
    </div>
  );
}
